import { useContext } from 'react';
import useAppState from '../../../state/appState';
import { ConfigContext } from '../../../context/ConfigContext';
import { CheckboxField, includes } from '@flos/react-ui';
import { VehicleZones as VehicleZonesType, FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { ClaimFormSubmitFieldValue } from '../../../types/claim-form-submit-field-value';

import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { SummaryRowTemplate } from '../../../helpers/templates';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';

import { getValue } from '../../values/Get';
import { getAdditionalZones, getValueMappingToValueId, getAdditionalZonesMapping } from './mapping';
import { getAllAvailableZones, getCarDamageAreaOptions, getImagePaths, getVehicleType } from './helpers';
import { VehicleType } from './helpers';
import './style.scss';

/**
 * The custom component receives value from either vehicleSelector or radiolist as element type
 *  DamageZones is stored in state for each vehicle type.
 */
type ComponentValue = {
    selectedVehicleType: string;
    damageZones: {
        [key: string]: [];
    };
};

const VehicleZones = ({ caption, elementId, selector }: VehicleZonesType) => {
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const appConfig = useContext(ConfigContext);
    const contentBase = appConfig.contentBase;

    const vehicleType: VehicleType = getVehicleType(formValues, selector);

    const value: ComponentValue = VehicleZones.getValueOrDefault(formValues, elementId);
    const additionalZonesMapping = getAdditionalZonesMapping();

    const onChangeValue = (selectedValue: string) => {
        const damageZones: string[] = value.damageZones[vehicleType] ?? [];
        if (damageZones && !damageZones.includes(selectedValue)) {
            damageZones.push(selectedValue);
        } else {
            const index = damageZones.indexOf(selectedValue);
            damageZones.splice(index, 1);
        }
        setFormValue(elementId, {
            selectedVehicleType: vehicleType,
            damageZones: {
                ...value.damageZones,
                [vehicleType]: damageZones
            }
        });
    };

    const imageMap = getImagePaths(contentBase);
    const additionalZones = additionalZonesMapping[vehicleType];

    return (
        <>
            {caption && <h6>{caption}</h6>}

            <div className="vehicle-zone-selector">
                {imageMap[vehicleType] && (
                    <div className="map">
                        <img src={imageMap[vehicleType]} className="image" alt="" />
                        {getCarDamageAreaOptions().map((option) => {
                            const isActive = includes(value.damageZones[vehicleType] ?? [], option.value);
                            return (
                                <div className={`box ${option.className}`} key={option.value}>
                                    <CheckboxField
                                        label={option.label}
                                        checked={isActive}
                                        onChange={() => {
                                            onChangeValue(option.value);
                                        }}
                                    ></CheckboxField>
                                </div>
                            );
                        })}
                    </div>
                )}
                {additionalZones && (
                    <div className="additional-zone">
                        {additionalZones.map((zone) => {
                            const isActive = includes(value.damageZones[vehicleType] ?? [], zone.value);
                            return (
                                <div className="box-container" key={zone.value}>
                                    <CheckboxField
                                        label={zone.label}
                                        checked={isActive}
                                        onChange={() => {
                                            onChangeValue(zone.value);
                                        }}
                                    ></CheckboxField>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

VehicleZones.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId) as VehicleZonesType;
    const { summaryCaption } = element;
    const valueObject: ComponentValue = getValue(formConfig, formValues, elementId);

    if (valueObject === undefined) {
        return <></>;
    }
    const selectedVehicleType = valueObject.selectedVehicleType;
    const damageZones = valueObject.damageZones[selectedVehicleType]
        .map(
            (zone) =>
                getCarDamageAreaOptions().find((area) => area.value === zone) ||
                Object.values(getAdditionalZones()).find((item) => item.value === zone)
        )
        .map((option) => option?.label)
        .join(', ');

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={damageZones} />;
};

VehicleZones.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string
): ClaimFormSubmitFieldValue => {
    const valueObj = VehicleZones.getValue(formValues, elementId);
    const valueStr = VehicleZones.getValueHuman(formValues, elementId);
    const valueIdMapping = getValueMappingToValueId();

    const vehicleType = valueObj.selectedVehicleType;
    const mappedValueIds = valueObj.damageZones[vehicleType].map(
        (zone: string) => valueIdMapping[zone as keyof typeof valueIdMapping]
    );
    const valueId = {
        zones: mappedValueIds.join(',')
    };

    const elementIdWithVehicleType = `${elementId}-${vehicleType}`;

    return getClaimFormSubmitValue('vehiclezone', elementIdWithVehicleType, caption, valueStr, valueId);
};

VehicleZones.getValueHuman = (formValues: FormValues, elementId: string) => {
    const value: ComponentValue = VehicleZones.getValue(formValues, elementId);
    const selectedVehicleType = value.selectedVehicleType;
    const damageZones = value.damageZones[selectedVehicleType];

    const zoneList = getAllAvailableZones();

    const zoneLabelsAreas = damageZones.map((zone: string) => zoneList.find((option) => option.value === zone)?.label);
    return zoneLabelsAreas.join(', ');
};

VehicleZones.getValueOrDefault = (formValues: FormValues, elementId: string): ComponentValue => {
    const valueObj: ComponentValue = VehicleZones.getValue(formValues, elementId);

    if (valueObj === undefined) {
        return {
            selectedVehicleType: '',
            damageZones: {}
        };
    }
    return valueObj;
};

VehicleZones.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const valueObject: ComponentValue = getValue(formConfig, formValues, elementId);

    if (!valueObject) {
        return false;
    }

    const elementConfig = getElementFromElementId(formConfig, elementId) as VehicleZonesType;
    const selectedVehicleType = getVehicleType(formValues, elementConfig.selector);

    if (
        selectedVehicleType &&
        valueObject.damageZones[selectedVehicleType] &&
        valueObject.damageZones[selectedVehicleType].length > 0
    ) {
        return true;
    }
    return false;
};

VehicleZones.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

export default VehicleZones;
